import {
    useInfiniteQuery,
    useMutation,
    useQuery,
    useQueryClient,
} from '@tanstack/react-query'
import { toast } from 'sonner'

import type {
    ListPackagingLogParams,
    PackagingLogEntryInput,
} from '@/lib/api/packaging-log-api'
import { packagingLogApi } from '@/lib/api/packaging-log-api'
import { productLotKeys } from '@/hooks/use-product-lot-query'
import { scentingLotKeys } from '@/hooks/use-scenting-lot-query'
import { m } from '@/lib/paraglide/messages'

// ============================================================================
// Sổ đóng gói 12,5g (#M-16)
// ============================================================================

export const packagingLogKeys = {
    all: ['packaging-log-entries'] as const,
    list: (params: ListPackagingLogParams) =>
        [...packagingLogKeys.all, 'list', params] as const,
    detail: (id: string) => [...packagingLogKeys.all, 'detail', id] as const,
    divergence: (id: string) =>
        [...packagingLogKeys.all, 'divergence', id] as const,
    sourceClaims: (excludeEntryId?: string) =>
        [
            ...packagingLogKeys.all,
            'source-claims',
            excludeEntryId ?? '',
        ] as const,
}

/** Một trang sổ = 20 mẻ, đủ phủ vài ngày làm việc mà vẫn tải nhanh trên máy tính bảng. */
export const PACKAGING_LOG_PAGE_SIZE = 20

/**
 * Sổ tải theo trang: mở lên chỉ có trang đầu (mẻ mới nhất ở trên), bấm "Xem
 * thêm" thì nối trang kế tiếp xuống dưới chứ không thay trang.
 */
export function usePackagingLogEntriesInfinite(
    params: Omit<ListPackagingLogParams, 'page' | 'per_page'> = {},
) {
    return useInfiniteQuery({
        queryKey: [...packagingLogKeys.all, 'infinite', params] as const,
        queryFn: ({ pageParam }) =>
            packagingLogApi.list({
                ...params,
                page: pageParam,
                per_page: PACKAGING_LOG_PAGE_SIZE,
            }),
        initialPageParam: 1,
        getNextPageParam: (lastPage) => {
            const meta = lastPage.meta
            if (!meta) return undefined
            return meta.page < meta.total_pages ? meta.page + 1 : undefined
        },
    })
}

export function usePackagingLogEntry(id?: string) {
    return useQuery({
        queryKey: packagingLogKeys.detail(id ?? ''),
        queryFn: () => packagingLogApi.get(id as string),
        enabled: !!id,
    })
}

/**
 * M-140: phần từng lô nguồn đang bị các mẻ CHƯA VÀO KHO khác giữ. Ô chọn lô trừ
 * sẵn để số hiện ra khớp số được phép ghi lúc Lưu. Luôn đọc mới khi mở form:
 * phiếu có thể vừa được duyệt/từ chối ở màn khác.
 */
export function usePackagingLogSourceClaims(excludeEntryId?: string) {
    return useQuery({
        queryKey: packagingLogKeys.sourceClaims(excludeEntryId),
        queryFn: () => packagingLogApi.sourceClaims(excludeEntryId),
        staleTime: 0,
        refetchOnMount: 'always',
    })
}

export function usePackagingLogDivergence(id?: string, enabled = true) {
    return useQuery({
        queryKey: packagingLogKeys.divergence(id ?? ''),
        queryFn: () => packagingLogApi.divergence(id as string),
        enabled: !!id && enabled,
    })
}

export function useCreatePackagingLogEntry() {
    const qc = useQueryClient()
    return useMutation({
        mutationFn: (req: PackagingLogEntryInput) =>
            packagingLogApi.create(req),
        onSuccess: () => {
            void qc.invalidateQueries({ queryKey: packagingLogKeys.all })
            toast.success(m.pkg_log_toast_saved())
        },
    })
}

export function useUpdatePackagingLogEntry() {
    const qc = useQueryClient()
    return useMutation({
        mutationFn: ({
            id,
            req,
        }: {
            id: string
            req: PackagingLogEntryInput
        }) => packagingLogApi.update(id, req),
        onSuccess: () => {
            void qc.invalidateQueries({ queryKey: packagingLogKeys.all })
            // M-150: sửa sổ khi phiếu chờ duyệt thì phiếu đổi theo — bỏ bản
            // phiếu đang nhớ để mở phiếu ra thấy ngay số mới.
            void qc.invalidateQueries({ queryKey: productLotKeys.inNotes() })
            toast.success(m.pkg_log_toast_updated())
        },
    })
}

export function useDeletePackagingLogEntry() {
    const qc = useQueryClient()
    return useMutation({
        mutationFn: (id: string) => packagingLogApi.remove(id),
        onSuccess: () => {
            void qc.invalidateQueries({ queryKey: packagingLogKeys.all })
            toast.success(m.pkg_log_toast_deleted())
        },
    })
}

export function useAdjustPackagingLogEntry() {
    const qc = useQueryClient()
    return useMutation({
        mutationFn: (id: string) => packagingLogApi.adjust(id),
        onSuccess: (res) => {
            // Điều chỉnh vừa sinh phiếu ở CẢ HAI kho nên phải làm mới cả hai,
            // nếu không danh sách phiếu và tồn lô vẫn là số cũ.
            void qc.invalidateQueries({ queryKey: packagingLogKeys.all })
            void qc.invalidateQueries({ queryKey: productLotKeys.all })
            void qc.invalidateQueries({ queryKey: scentingLotKeys.all })
            toast.success(
                m.pkg_log_toast_adjusted({ n: res.data?.length ?? 0 }),
            )
        },
    })
}
